// Tarifs officiels 1000ARTS (saison 2026/2027)
// Source: documents partagés par 1000ARTS
//
// PRINCIPE — le prix d'une inscription ne dépend que de QUATRE choses :
//   1. la case « adulte » du formulaire ;
//   2. la date de naissance saisie dans le formulaire ;
//   3. la durée du cours, RECALCULÉE depuis « Début » et « Fin » du planning ;
//   4. les colonnes FORFAIT et PRIX_FORFAIT du planning.
// Aucune autre cellule du tableur n'a d'effet sur l'argent : « Âge min / Âge max / Âge
// affiché » disent seulement qui a le droit de s'inscrire, « Cours / Discipline / Niveau »
// sont des libellés d'affichage, et la colonne « Durée (min) » est ignorée (elle a déjà
// été écrasée à la main une fois, ligne 50, sans que personne ne le voie).
import { courseSlug } from "./planningHelpers";
import { selectionGroups, forfaitOf, estCompetition } from "./forfaits";
import { isAdultForTarifs } from "./ages";

// Barème par durée. Les valeurs ci-dessous sont le REPLI : dès que `GET /api/reglages`
// répond, la grille saisie dans la console les remplace (voir `appliquerBaremeConsole`).
// L'école change donc un prix dans Réglages et le site suit, sans redéploiement.
const PRICE_BY_DURATION_CHILDREN = {
  30: 200, 45: 290, 60: 390, 75: 420, 90: 450,
};
const PRICE_BY_DURATION_ADULTS = {
  60: 450, 75: 480, 90: 510, // adultes : 1h=450, 1h15=480, 1h30=510
};
// Exportés pour le script de build qui génère les Offer schema.org (source unique).
export { PRICE_BY_DURATION_CHILDREN, PRICE_BY_DURATION_ADULTS };

// Les objets sont modifiés SUR PLACE : tout ce qui les a déjà importés voit la mise à jour.
// Une grille vide ou illisible est ignorée — on garde le barème de repli plutôt que
// d'afficher des prix à zéro.
export function appliquerBaremeConsole(enfants, adultes) {
  const nombres = (grille) => Object.fromEntries(
    Object.entries(grille || {})
      .map(([duree, prix]) => [Number(duree), Number(prix)])
      .filter(([duree, prix]) => Number.isFinite(duree) && Number.isFinite(prix) && prix > 0));
  const e = nombres(enfants);
  const a = nombres(adultes);
  if (Object.keys(e).length) Object.assign(PRICE_BY_DURATION_CHILDREN, e);
  if (Object.keys(a).length) Object.assign(PRICE_BY_DURATION_ADULTS, a);
}

// Les seules durées tarifées. Une durée hors de cette liste n'a pas de prix : on refuse
// le cours plutôt que d'inventer un montant approximatif dans un e-mail de confirmation.
export const DUREES_TARIFEES = [30, 45, 60, 75, 90];

// Durée hebdomadaire en minutes, calculée depuis « Début » et « Fin ». On ne lit JAMAIS
// la colonne « Durée (min) » : c'est une formule que l'école peut écraser à la main.
export function durationFromSchedule(course) {
  const toMinutes = (hhmm) => {
    const m = /^(\d{1,2})[:h.](\d{2})$/.exec(String(hhmm || "").trim());
    if (!m) return null;
    return Number(m[1]) * 60 + Number(m[2]);
  };
  const start = toMinutes(course?.start);
  const end = toMinutes(course?.end);
  if (start === null || end === null) return null;
  const d = end - start;
  return d > 0 ? d : null;
}

export function isDureeTarifee(course) {
  return DUREES_TARIFEES.includes(durationFromSchedule(course));
}

// Prix semestriel d'un cours pour une personne donnée.
// `isAdultPerson` : la personne inscrite a 18 ans ou plus (case du formulaire ou date de
// naissance). Retourne null si la durée n'est pas tarifée — l'appelant doit refuser.
export function priceForSession(course, isAdultPerson = false) {
  const minutes = durationFromSchedule(course);
  if (!DUREES_TARIFEES.includes(minutes)) return null;
  if (isAdultPerson) {
    // Le barème adulte ne couvre que 1h / 1h15 / 1h30. Pour une durée plus courte
    // (aucune aujourd'hui dans le planning), on applique le barème enfants plutôt que
    // d'inventer un prix.
    return PRICE_BY_DURATION_ADULTS[minutes] ?? PRICE_BY_DURATION_CHILDREN[minutes];
  }
  return PRICE_BY_DURATION_CHILDREN[minutes];
}

export const PARAMETRES = {
  taxe_admin_premiere_inscription: 20,
  reduction_2eme_enfant: 25,
  reduction_3eme_enfant: 35,
  reduction_4eme_enfant: 45,
  prix_cours_essai: 10,
  cours_essai_gratuit: true,
  // Costume de spectacle (facturé au 2ᵉ semestre) et bon de parrainage : affichés par la
  // page Tarifs, remplacés par les montants des Réglages comme le reste.
  costume_enfant: 60,
  costume_adulte: 40,
  parrainage: 50,
};

// Réduction d'accumulation : REPLI. Les marches réelles sont réglées dans la console
// (Réglages → Paramètres et règles) et arrivent par `GET /api/reglages`. Exportée pour la
// page Tarifs, qui affiche les trois marches.
export const ACCUMULATION = { cours_2: 50, cours_3: 100, cours_suivant: 150 };

// Montants de règlement saisis dans la console : ils remplacent les valeurs de repli
// ci-dessus, sur place, pour tout le site (formulaires, /tarifs, e-mails).
// Carte 10 cours : 10 séances à l'unité, pour les disciplines sans engagement semestriel.
// Prix, disciplines concernées et VRAIES dates de validité viennent de la console
// (`GET /api/reglages` → carte_10). La carte s'arrête à la fin du semestre en cours — ce
// n'est pas « six mois », ce que le site annonçait à tort jusqu'au 05.10.2026.
export const CARTE_10 = {
  prix: 250,
  nb_seances: 10,
  disciplines: ["Zumba", "Reggaeton"],
  mots_cles: ["hip hop freestyle"],
  echeance: "",
  semestre_courant: "",
  semestres: {},
};

const sansAccent = (t) => String(t || "").normalize("NFKD").replace(/[\u0300-\u036f]/g, "")
  .replace(/[^a-zA-Z ]/g, " ").replace(/\s+/g, " ").trim().toLowerCase();

// Ce cours peut-il se prendre à la carte plutôt qu'au semestre ?
export function carte10Eligible(course) {
  if (!course) return false;
  const discipline = sansAccent(course.discipline);
  if (discipline && CARTE_10.disciplines.some((d) => sansAccent(d) === discipline)) return true;
  const nom = sansAccent(course.name || course.nom);
  return CARTE_10.mots_cles.some((m) => sansAccent(m) && nom.includes(sansAccent(m)));
}

// « valables pour le semestre en cours uniquement » — une phrase qui reste vraie toute
// l'année (semestre 1 comme semestre 2), là où « 6 mois » était faux : une carte prise
// en décembre ne donne qu'un mois et demi de cours. Les dates exactes des deux semestres
// sont affichées à côté, par `texteSemestresCarte10()`.
export function texteValiditeCarte10() {
  return "valables pour le semestre en cours uniquement";
}

const jourFr = (iso) => (iso ? String(iso).slice(0, 10).split("-").reverse().join(".") : "");

// [« Semestre 1 : 25.08.2026 → 31.01.2027 », « Semestre 2 : … »] — les vraies dates de la
// console, pour que la famille sache jusqu'à quand court la carte qu'elle achète.
export function texteSemestresCarte10() {
  return [1, 2]
    .map((n) => [n, CARTE_10.semestres?.[String(n)] || {}])
    .filter(([, s]) => s.debut && s.fin)
    .map(([n, s]) => `Semestre ${n} : ${jourFr(s.debut)} → ${jourFr(s.fin)}`);
}

// « semestre 1 : du 24.08.2026 au 31.01.2027 » — le semestre qui court aujourd'hui, avec
// ses vraies dates (calendrier de la console) : le texte change tout seul chaque semestre.
// Vide si la console ne les connaît pas encore : on n'écrit alors pas de date.
export function texteSemestreCourantCarte10() {
  const n = CARTE_10.semestre_courant;
  const s = (n && CARTE_10.semestres?.[n]) || {};
  return s.debut && s.fin ? `semestre ${n} : du ${jourFr(s.debut)} au ${jourFr(s.fin)}` : "";
}

export function appliquerCarte10Console(carte) {
  if (!carte) return;
  if (Number.isFinite(Number(carte.prix))) CARTE_10.prix = Number(carte.prix);
  if (Number.isFinite(Number(carte.nb_seances))) CARTE_10.nb_seances = Number(carte.nb_seances);
  if (Array.isArray(carte.disciplines) && carte.disciplines.length) CARTE_10.disciplines = carte.disciplines;
  if (Array.isArray(carte.mots_cles)) CARTE_10.mots_cles = carte.mots_cles;
  CARTE_10.echeance = carte.echeance || "";
  CARTE_10.semestre_courant = String(carte.semestre_courant || "");
  CARTE_10.semestres = carte.semestres || {};
}

export function appliquerParametresConsole(parametres, accumulation) {
  const nombres = (o) => Object.fromEntries(
    Object.entries(o || {})
      .map(([cle, valeur]) => [cle, Number(valeur)])
      .filter(([, valeur]) => Number.isFinite(valeur)));
  const p = nombres(parametres);
  const a = nombres(accumulation);
  if (Object.keys(p).length) Object.assign(PARAMETRES, p);
  if (Object.keys(a).length) Object.assign(ACCUMULATION, a);
}

// Stages : dates, prix et ouverture viennent de la console (Réglages → Catalogue des
// stages). Les textes et les photos restent dans le site. Un stage dont la date de fin est
// passée est fermé automatiquement.
// Date du jour en heure LOCALE (Suisse) : `toISOString()` donne la date UTC, encore celle
// de la veille entre minuit et 1–2 h du matin.
export function aujourdhuiLocal(now = new Date()) {
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const j = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${m}-${j}`;
}

// Premier jour d'un stage à partir de son dernier jour et du texte de ses dates
// (« Du 12 au 16 octobre 2026 », « Du 29 juin au 3 juillet 2026 », « Du 29.06 au
// 03.07.2026 ») : le premier nombre du texte est le jour de début ; s'il dépasse le jour de
// fin, le stage a commencé le mois précédent. Chaîne vide si illisible.
function debutDepuisTexte(texte, finIso) {
  const fin = String(finIso || "").slice(0, 10);
  const m = String(texte || "").match(/(\d{1,2})/);
  if (!m || !/^\d{4}-\d{2}-\d{2}$/.test(fin)) return "";
  const jour = Number(m[1]);
  const [a, mo, j] = fin.split("-").map(Number);
  const d = jour <= j ? new Date(a, mo - 1, jour) : new Date(a, mo - 2, jour);
  return aujourdhuiLocal(d);
}

// Un stage passé OU déjà commencé ne prend plus d'inscription : on ne s'inscrit pas à
// une semaine en cours. Vrai aussi sans la console (dates de repli ci-dessous).
export function stageFerme(stage, now = new Date()) {
  if (!stage) return true;
  if (stage.status === "closed") return true;
  const jour = aujourdhuiLocal(now);
  if (stage.debut_iso && stage.debut_iso <= jour) return true;
  if (stage.fin_iso && stage.fin_iso < jour) return true;
  return false;
}

function fermerSiPasse(stage, now = new Date()) {
  if (stage.status !== "closed" && stageFerme(stage, now)) {
    stage.status = "closed";
    stage.status_label = (stage.fin_iso && stage.fin_iso >= aujourdhuiLocal(now))
      ? "Stage en cours — inscriptions fermées"
      : "Stage terminé — inscriptions fermées";
  }
}

// Les dates de repli suffisent à fermer un stage passé ou en cours, même si
// `/api/reglages` ne répond pas.
export function fermerStagesPasses(now = new Date()) {
  STAGES.forEach((s) => fermerSiPasse(s, now));
}

export function appliquerStagesConsole(liste) {
  const aujourdhui = aujourdhuiLocal();
  (liste || []).forEach((s) => {
    const stage = STAGES.find((x) => x.sheet_id === s.id);
    if (!stage) return;
    if (s.dates) stage.dates_label = s.dates;
    if (s.fin) {
      stage.fin_iso = String(s.fin).slice(0, 10);
      stage.debut_iso = debutDepuisTexte(s.dates || stage.dates_label, stage.fin_iso) || stage.debut_iso;
      stage.status = s.fin < aujourdhui ? "closed" : "open";
    }
    fermerSiPasse(stage);
    if (stage.status === "closed" && !stage.status_label) stage.status_label = "Stage terminé — inscriptions fermées";
    (stage.options || []).forEach((o) => {
      if (o.id === "membre" && s.prix_membre) o.price = s.prix_membre;
      if (o.id === "non-membre" && s.prix_non_membre) o.price = s.prix_non_membre;
    });
  });
}

// Stages catalog — placeholder before Google Sheet Phase 2.
// Chaque stage définit son propre set d'options (label + prix).
// `status`: "open" (inscriptions ouvertes) | "closed" (terminé / plein).
// `dates_label` = affiche libre ; `ages_label` = affiche libre.
// IMPORTANT — champs repris tels quels dans les e-mails de confirmation (client + 1000 ARTS) :
//   title, dates_label, hours_label, location_label, options[].label / .price
// Tout nouveau stage (Pâques, été…) doit donc renseigner hours_label et location_label,
// sinon les horaires apparaîtront comme « — » dans les e-mails.
// En Phase 2 : cette liste viendra de l'onglet "Stages" du Google Sheet.
export const STAGES = [
  {
    id: "automne-2026",
    sheet_id: "stage_automne_2026",
    title: "Stage d'automne — Hip-hop & Comédie musicale",
    dates_label: "Du 12 au 16 octobre 2026",
    // Dates de repli (la console les remplace) : un stage commencé ou passé se ferme seul.
    debut_iso: "2026-10-12",
    fin_iso: "2026-10-16",
    hours_label: "9h00 – 16h00 (accueil dès 8h45)",
    location_label: "1000 ARTS · Chemin des Dailles 10E, 1053 Cugy VD",
    ages_label: "5 - 14 ans (2 groupes)",
    status: "open",
    options: [
      { id: "membre",     label: "Tarif membre (semaine complète · 5 jours)",     price: 450 },
      { id: "non-membre", label: "Tarif non-membre (semaine complète · 5 jours)", price: 490 },
    ],
  },
  {
    id: "summer-2026",
    sheet_id: "stage_ete_2026",
    title: "Stage d'été — Tennis & Hip-hop",
    dates_label: "Du 29.06 au 03.07.2026",
    debut_iso: "2026-06-29",
    fin_iso: "2026-07-03",
    hours_label: "9h00 – 17h00 (matin & après-midi)",
    location_label: "1000 ARTS · Chemin des Dailles 10E, 1053 Cugy VD",
    ages_label: "6 - 12 ans",
    status: "closed",
    status_label: "Stage terminé — inscriptions fermées",
    options: [
      { id: "tennis-hiphop-full", label: "Tennis + Hip-hop (journée complète)", price: 700 },
      { id: "hiphop-only",        label: "Hip-hop uniquement (après-midi)",     price: 390 },
    ],
  },
];

fermerStagesPasses();

// Legacy alias — le formulaire Stage utilise désormais STAGES[stage].options.
export const STAGE_OPTIONS = STAGES[0].options;

// NE PAS RÉINTRODUIRE de détection « cours d'adultes » à partir du planning.
// Historique : le barème a d'abord été choisi sur « Âge min >= 18 » — l'école a abaissé
// l'âge à 15/16 ans le 14.08.2026 pour accueillir les ados, et tous les cours adultes
// sont passés en silence au tarif enfant. Le remplacer par la lecture du libellé
// « Adultes » ne faisait que déplacer le piège sur une autre colonne. Depuis le
// 17.08.2026, seules la case « adulte » du formulaire et la date de naissance décident
// du barème (voir lib/ages.js).

// Prix d'une séance telle qu'elle est réellement vendue : un entraînement appartenant à un
// forfait Compétition/Compagnie ne se vend pas seul → c'est le prix du forfait qui compte,
// quel que soit l'âge et la durée.
export function sessionPrice(course, isAdultPerson = false) {
  const f = forfaitOf(course);
  return f ? f.price : priceForSession(course, isAdultPerson);
}

// Libellé d'une option du menu déroulant. Le prix dépend de la personne, donc le libellé
// est construit au moment de l'affichage, pour l'élève concerné.
export function courseOptionLabel(course, isAdultPerson = false) {
  const forfait = forfaitOf(course);
  const priceLabel = forfait
    ? `Forfait CHF ${forfait.price} / semestre (2 entraînements)`
    : `CHF ${priceForSession(course, isAdultPerson)} / semestre`;
  return `${course.day} ${course.start} — ${course.name}${course.level ? ` · Niveau ${course.level}` : ""} — ${priceLabel}`;
}

// Construit les options de cours du formulaire semestriel depuis le planning du Sheet.
// `id` = ID_COURS (clé primaire du Sheet), jamais affiché.
// Volontairement SANS prix : il dépend de l'élève (âge / case adulte) et se calcule au
// moment de l'affichage et du calcul, jamais figé dans l'option.
export function buildCourseOptions(courses) {
  return (courses || []).map((c) => {
    return {
      id: c.id,
      slug: courseSlug(c),
      name: c.name,
      day: c.day,
      start: c.start,
      end: c.end,
      time: `${c.start} → ${c.end}`,
      room: c.room,
      age_label: c.age_label,
      age_min: c.age_min,
      age_max: c.age_max,
      level: c.level,
      discipline: c.discipline,
      duration_min: durationFromSchedule(c),
      etat_places: c.etat_places,
      message_site: c.message_site,
      forfait: c.forfait || "",
      forfait_price: c.forfait_price || null,
      late_join: c.late_join || "",
    };
  });
}

// Pass illimité — options disponibles pendant l'inscription semestrielle.
// Les prix ci-dessous sont le REPLI : dès que `GET /api/reglages` répond, ceux des
// Réglages de la console (Tarifs et forfaits → Pass illimités) les remplacent, sur place
// (voir `appliquerPassesConsole`). La facture applique ces mêmes prix.
export const PASSES = [
  {
    id: "infinity-enfant",
    label: "Infinity Pass Enfant",
    price: 1490,
    audience: "enfant",
    tagline: "Accès total (compétition et compagnie inclus)",
    includes: [
      "Accès à tous les cours (hors cours privés)",
      "Réductions stages & workshops",
      "Priorité aux admissions",
    ],
  },
  {
    id: "golden-enfant",
    label: "Golden Pass Enfant",
    price: 1190,
    audience: "enfant",
    tagline: "Accès étendu (hors compétition et compagnie)",
    includes: [
      "Tous les cours (hors compétition, compagnie & privés)",
      "Réductions stages & workshops",
      "Priorité aux admissions",
    ],
  },
  {
    id: "infinity-adulte",
    label: "Infinity Pass Adulte",
    price: 1390,
    audience: "adulte",
    tagline: "Accès total adulte (hors cours privés)",
    includes: [
      "Tous les cours (hors cours privés)",
      "Réductions stages & workshops",
    ],
  },
];

// Détermine si un pass est plausible pour l'enfant/adulte donné en fonction de sa date de naissance.
// Règle : les Pass "adulte" sont proposés à partir de 16 ans, les Pass "enfant" jusqu'à 30 ans.
export function isPassEligibleForChild(pass, child) {
  if (!child?.birth_date) return true;
  const b = new Date(child.birth_date);
  if (Number.isNaN(b.getTime())) return true;
  const now = new Date();
  let age = now.getFullYear() - b.getFullYear();
  const m = now.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age -= 1;
  if (pass.audience === "adulte") return age >= 16;
  return age <= 30;
}

export function getPassById(passId) {
  return PASSES.find((p) => p.id === passId) || null;
}

// Prix des pass réglés dans la console (`GET /api/reglages` → pass). Modifiés SUR PLACE :
// la page Tarifs, le formulaire et la suggestion de pass voient le même prix que la
// facture. Un pass inconnu du site ou un prix illisible est ignoré (le repli reste).
export function appliquerPassesConsole(liste) {
  if (!Array.isArray(liste)) return;
  liste.forEach((p) => {
    const pass = PASSES.find((x) => x.id === p?.id);
    const prix = Number(p?.prix);
    if (pass && Number.isFinite(prix) && prix > 0) pass.price = prix;
  });
}
// Un élève compte dans la réduction famille s'il est MINEUR et s'il suit au moins un cours
// régulier. Règles tranchées par l'école (18.08.2026) :
//   - un adulte ne compte jamais, ni comme bénéficiaire ni dans le décompte ;
//   - un enfant dont TOUTES les lignes sont de la compétition ou de la compagnie ne compte
//     pas — mais s'il a en plus un cours régulier, il compte normalement ;
//   - la carte 10 cours n'ouvre aucun droit.
function compteDansLaFamille(sub) {
  if (sub.adulte) return false;
  if (sub.pass && /carte/i.test(sub.pass.id)) return false;
  return (sub.lines || []).some((g) => g.type === "course" && !estCompetition(g.courses[0])
    && !g.courses[0]?.carte10);
}

function familyDiscount(numChildren) {
  if (numChildren >= 4) return PARAMETRES.reduction_4eme_enfant;
  if (numChildren === 3) return PARAMETRES.reduction_3eme_enfant;
  if (numChildren === 2) return PARAMETRES.reduction_2eme_enfant;
  return 0;
}

// Réduction d'accumulation par élève (CHF 50 au 2ᵉ cours, 100 au 3ᵉ, 150 par cours suivant).
// Exportée : la suggestion de pass doit comparer le pass au montant RÉELLEMENT payé,
// réductions déduites, sinon elle annonce des économies qui n'existent pas.
// Les cours de discipline Compétition ou Compagnie ne comptent PAS dans le décompte
// (page Tarifs : « Non valable sur les cours de compétition et de compagnie »), qu'ils
// soient vendus en forfait ou à l'unité.
// Le détail de la réduction, pour qu'on voie d'où vient le montant : « 50.– sur le 2ᵉ cours
// + 100.– sur le 3ᵉ » (3 cours = 150.–). Une famille lisait « 3 cours : 150.– » alors que
// les Réglages disent « 3ᵉ cours : 100 » (question de l'école, 09.10.2026).
export function detailAccumulation(numCourses) {
  if (numCourses <= 1) return "";
  const parts = [`${ACCUMULATION.cours_2}.– sur le 2ᵉ cours`];
  if (numCourses >= 3) parts.push(`${ACCUMULATION.cours_3}.– sur le 3ᵉ`);
  if (numCourses >= 4) {
    const n = numCourses - 3;
    parts.push(`${ACCUMULATION.cours_suivant}.– ${n > 1 ? `× ${n} ` : ""}sur ${n > 1 ? "les suivants" : "le 4ᵉ"}`);
  }
  return parts.join(" + ");
}

export function accumulationDiscount(numCourses) {
  if (numCourses <= 1) return 0;
  if (numCourses === 2) return ACCUMULATION.cours_2;
  return ACCUMULATION.cours_2 + ACCUMULATION.cours_3
    + (numCourses - 3) * ACCUMULATION.cours_suivant;
}

// Coût d'une sélection de cours pour un élève : les forfaits comptent une seule fois à
// leur prix forfaitaire et sont exclus de la réduction d'accumulation. Le prix de chaque
// cours est recalculé ici pour la personne concernée — on ne fait jamais confiance à un
// prix déjà posé sur l'objet cours, qui pourrait dater d'avant un changement d'âge ou de
// la case « adulte ».
export function selectionPricing(courses, isAdultPerson = false) {
  const groups = selectionGroups(courses).map((g) => ({
    ...g,
    // Carte 10 cours : prix fixe de la carte, quelle que soit la durée du cours.
    price: g.type === "forfait" ? g.price
      : g.courses[0]?.carte10 ? CARTE_10.prix
      : sessionPrice(g.courses[0], isAdultPerson),
  }));
  const subtotal = groups.reduce((s, g) => s + (g.price || 0), 0);
  // Seuls les cours réguliers comptent : la discipline Compétition (forfaits ET cours du
  // mercredi vendus à l'unité) n'ouvre aucun droit à la réduction d'accumulation.
  // La carte 10 cours n'ouvre pas droit à la réduction d'accumulation (condition de
  // l'école, reprise de /tarifs).
  const accumCount = groups.filter((g) => g.type === "course" && !estCompetition(g.courses[0])
    && !g.courses[0]?.carte10).length;
  return { groups, subtotal, accumCount, accumDisc: accumulationDiscount(accumCount) };
}

// Un cours de compétition ou de compagnie n'est PAS couvert par le Golden Pass.
export function passCoversCourses(pass, courses) {
  if (pass.id !== "golden-enfant") return true;
  return !(courses || []).some((c) => {
    const d = c?.discipline || "";
    return d === "Compétition" || d === "Compagnie";
  });
}

// Cherche le pass le MOINS CHER qui devient réellement avantageux pour cette sélection.
// `eligiblePasses` : les pass proposables à cette personne (adulte vs enfant, âge).
// Retourne null si aucun pass ne fait économiser d'argent.
export function findBetterPass(courses, eligiblePasses, isAdultPerson = false) {
  const list = courses || [];
  if (list.length === 0) return null;
  const { subtotal, accumDisc } = selectionPricing(list, isAdultPerson);
  const realCost = subtotal - accumDisc;
  const candidates = (eligiblePasses || PASSES)
    .filter((p) => passCoversCourses(p, list) && p.price < realCost)
    .sort((a, b) => a.price - b.price);
  if (candidates.length === 0) return null;
  const pass = candidates[0];
  return { pass, realCost, savings: realCost - pass.price };
}

export function computeFamilyPricing(children, firstRegistration, isAdultSelf = false) {
  let childSubtotals = [];
  let total = 0;
  let accumulationTotal = 0;
  children.forEach((child, idx) => {
    const courses = child.courses || [];
    const pass = child.pass_id ? getPassById(child.pass_id) : null;
    // Barème : la case « adulte » du formulaire, sinon l'âge au début du semestre.
    const adulte = isAdultForTarifs(child, isAdultSelf);
    if (pass) {
      // Un pass illimité remplace la somme des cours et neutralise la réduction accumulation.
      childSubtotals.push({
        name: `${child.first_name} ${child.last_name}`.trim() || `Enfant ${idx + 1}`,
        courses, subtotal: pass.price, accumDisc: 0, accumCount: 0,
        lines: selectionPricing(courses, adulte).groups, pass, adulte,
      });
      total += pass.price;
    } else {
      const { groups, subtotal, accumCount, accumDisc } = selectionPricing(courses, adulte);
      childSubtotals.push({
        name: `${child.first_name} ${child.last_name}`.trim() || `Enfant ${idx + 1}`,
        courses, subtotal, accumDisc, accumCount, lines: groups, pass: null, adulte,
      });
      total += subtotal;
      accumulationTotal += accumDisc;
    }
  });
  const enfantsEligibles = childSubtotals.filter(compteDansLaFamille).length;
  const familyDisc = familyDiscount(enfantsEligibles);
  const adminFee = PARAMETRES.taxe_admin_premiere_inscription;
  // Taxe administrative : CHF 20 par ÉLÈVE qui entre à l'école, une seule fois dans sa vie
  // (et non par famille). Une famille qui inscrit deux nouveaux enfants paie donc CHF 40, et
  // si un seul des deux est nouveau, CHF 20. `firstRegistration` reste utilisé quand un
  // adulte s'inscrit pour lui-même : il n'y a alors pas de case par élève.
  const nouveauxEleves = children.filter((c) => c.nouvel_eleve).length;
  // En mode famille, SEULES les cases par enfant comptent : `firstRegistration` vaut `true`
  // par défaut dans le formulaire et sa case n'existe plus ici — s'en servir de repli
  // facturait 20 francs fantômes à une famille dont aucun élève n'est nouveau.
  const nbTaxes = isAdultSelf ? (firstRegistration ? 1 : 0) : nouveauxEleves;
  const adminFeeTotal = nbTaxes * adminFee;
  const final = total - accumulationTotal - familyDisc + adminFeeTotal;
  return { childSubtotals, total, discount: familyDisc, enfantsEligibles,
           accumulation: accumulationTotal, adminFee: adminFeeTotal,
           adminFeeCount: nbTaxes, adminFeeUnit: adminFee, final };
}
